import { useCallback, useEffect, useMemo, useState } from "react";
import style from "./photos.module.scss";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getAdditionalPhotosAsync, setSelectedPhotosToCompare } from "../../../../app/patientSlice";
import Loader from "../../../../static/images/spinner.svg";
import { getBaseUrl } from "../../../../utils";
import { ZoomPhoto } from "./zoomPhoto";

export const AdditionalPhotos = ({ primaryImage, assessmentNumber }) => {
  const dispatch = useDispatch();
  const { patientId } = useParams();

  const [selected, setSelected] = useState(null);
  const [selectedRecordType, setSelectedRecordType] = useState("");
  const [imageLoading, setImageLoading] = useState(true);
  const [imageList, setImageList] = useState([]);
  const [thumbLoading, setThumbLoading] = useState({});

  const { loading, response } = useSelector((state) => state.patient.additionalPhotos);
  const selectedPhotosToCompare = useSelector((state) => state.patient.selectedPhotosToCompare);

  const supportImages = useMemo(() => response?.supportImages || [], [response]);

  useEffect(() => {
    if (selected) {
      setImageLoading(true);
    }
  }, [selected]);

  useEffect(() => {
    if (patientId && assessmentNumber) {
      dispatch(getAdditionalPhotosAsync({ patient_id: patientId, assessment_id: assessmentNumber }));
    }
  }, [dispatch, patientId, assessmentNumber]);

  useEffect(() => {
    setSelected(primaryImage);
    setSelectedRecordType("Primary Image");
    setImageList([primaryImage, ...supportImages]);
  }, [primaryImage, supportImages]);

  const handleAdditionalPhoto = useCallback((image, index) => {
    setSelected(image);
    setSelectedRecordType(`Photo ${index + 1}`);
  }, []);

  const handleImageSelect = (e, image) => {
    const isChecked = e.target.checked;
    const formattedImage = getBaseUrl(image);

    if (isChecked) {
      const updatedPhotos = selectedPhotosToCompare?.includes(formattedImage)
        ? selectedPhotosToCompare
        : [...(selectedPhotosToCompare || []), formattedImage];

      dispatch(setSelectedPhotosToCompare(updatedPhotos));
    } else {
      const updatedPhotos = selectedPhotosToCompare?.filter((img) => img !== formattedImage);
      dispatch(setSelectedPhotosToCompare(updatedPhotos));
    }
  };

  return (
    <div className={style.additional_photos_root}>
      {loading ? (
        <div className="loader col-12">
          <img src={Loader} alt="loading" loading="lazy" />
        </div>
      ) : (
        <>
          <div className={`p-2 ${style.additional_scroll}`} style={{ width: "25%" }}>
            {imageList.map((image, index) => {
              const isSelected = selected === image;
              const isChecked = selectedPhotosToCompare?.includes(getBaseUrl(image));

              return (
                <div
                  key={image || index}
                  className={`card ${style.image_card} mb-2 ${isSelected ? style.selected : ""}`}
                  onClick={() => handleAdditionalPhoto(image, index)}
                >
                  <div className={style.img_wrapper}>
                    {thumbLoading[index] !== false && <div className={style.thumb_shimmer} aria-hidden="true" />}
                    {image === primaryImage && <span className={style.primary_badge}>Primary</span>}
                    <img
                      loading="lazy"
                      src={image}
                      alt={`image-${index}`}
                      style={{ opacity: thumbLoading[index] === false ? 1 : 0 }}
                      onLoad={() => setThumbLoading((prev) => ({ ...prev, [index]: false }))}
                    />
                    <input
                      type="checkbox"
                      className={style.select_checkbox}
                      checked={isChecked}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => handleImageSelect(e, image)}
                      data-testid={`select-photo${index}`}
                    />
                    {image !== primaryImage && (
                      <button
                        aria-hidden="true"
                        onClick={(e) => {
                          e.stopPropagation();
                          // TODO: handleDelete(image, index);
                        }}
                        data-testid={`delete-photo${index}`}
                      >
                        <i className={`fa fa-trash fa-lg ${style.delete_icon}`}></i>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {selected && (
            <div className="w-100 p-2">
              <ZoomPhoto
                image={selected}
                title={selectedRecordType}
                loading={imageLoading}
                setLoading={setImageLoading}
                classes={{
                  outerClass: "rounded",
                  innerClass: "position-relative mx-4 mt-4 mb-2",
                  shimmerClass: style.skeleton_photo, 
                }}
                containImage
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};
